/**
 * Canvelle cart picks (HORIZON-THEME.md §1.34): the Add buttons on the tiles rendered by
 * snippets/canvelle-cart-picks.liquid inside the cart drawer.
 *
 * One delegated click listener, so the buttons keep working after every drawer redraw. An
 * add posts to /cart/add.js asking for the cart sections back, then announces the change
 * the same way product-form.js does: a CartLinesUpdateEvent whose resolved detail carries
 * those sections. The drawer, the header count and the cart lines all redraw from that;
 * the redrawn drawer recomputes the picks, so the added product drops out of them.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ADD_SELECTOR = '[data-cv-pick-add]';
const MESSAGE_DURATION = 5000;

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest(ADD_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;
  if (button.getAttribute('aria-busy') === 'true') return;

  addPick(button);
});

/** @param {HTMLButtonElement} button */
async function addPick(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const picks = button.closest('.cv-cart-picks');
  const position = picks ? [...picks.querySelectorAll(ADD_SELECTOR)].indexOf(button) : -1;
  const dialog = button.closest('dialog');
  const name = button.closest('li')?.querySelector('.cv-cart-picks__name')?.textContent?.trim() ?? '';
  const label = button.innerHTML;

  button.setAttribute('aria-busy', 'true');
  button.setAttribute('aria-disabled', 'true');
  button.innerHTML = '<span class="cv-cart-picks__add-label">Adding…</span>';
  clearMessage(button);

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  let added = {};
  let problem = '';
  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
    });
    added = await response.json().catch(() => ({}));
    if (!response.ok || added.status) problem = errorText(added);
  } catch {
    problem = errorText(null);
  }

  if (problem) {
    button.innerHTML = label;
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
    showMessage(button, problem);
    announce(dialog, problem);
    return;
  }

  const cart = await fetchCart().catch(() => null);
  const update = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: update.promise,
    })
  );

  update.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
    detail: {
      items: cart?.items ?? [],
      source: 'canvelle-cart-picks',
      sourceId: 'canvelle-cart-picks',
      itemCount: 1,
      productId: String(added.items?.[0]?.product_id ?? ''),
      sections: added.sections,
      didError: false,
    },
  });

  announce(dialog, name ? `${name} added to your cart` : 'Added to your cart');

  // The drawer redraw runs off the same promise; let it land, then put focus back where
  // the shopper was if the tile they pressed is gone.
  await update.promise;
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  restoreFocus(dialog, position);
}

/** The cart as /cart.js returns it, through the drawer's own fetch when it is there. */
async function fetchCart() {
  const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (cartItems && typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();

  const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

/**
 * @param {HTMLDialogElement | null} dialog
 * @param {number} position
 */
function restoreFocus(dialog, position) {
  const active = document.activeElement;
  if (active && active !== document.body && active.isConnected) return;

  const scope = dialog ?? document;
  const buttons = scope.querySelectorAll(`.cv-cart-picks ${ADD_SELECTOR}`);
  const target =
    buttons[Math.max(0, Math.min(position, buttons.length - 1))] ??
    scope.querySelector('#CvCartPicksHeading') ??
    scope.querySelector('.cart-items__title');

  if (target instanceof HTMLElement) target.focus();
}

/**
 * A polite status message for screen readers. It lives on the drawer's <dialog> itself,
 * outside the part the cart redraw replaces, and inside the modal so it is not inert.
 * @param {HTMLDialogElement | null} dialog
 * @param {string} text
 */
function announce(dialog, text) {
  const host = dialog ?? document.body;
  let region = host.querySelector(':scope > .cv-cart-picks-status');
  if (!region) {
    region = document.createElement('div');
    region.className = 'cv-cart-picks-status visually-hidden';
    region.setAttribute('role', 'status');
    host.append(region);
  }
  region.textContent = '';
  const live = region;
  setTimeout(() => {
    live.textContent = text;
  }, 100);
}

/**
 * Shopify's 422 text, shortened where it is long: "The product 'Flat Pouch - Black /
 * Small' is already sold out." becomes "Sorry, this just sold out."; quantity limits
 * ("You can only add 3 …") pass through.
 * @param {{ description?: string, message?: string } | null} reply
 */
function errorText(reply) {
  const text = String(reply?.description || reply?.message || '');
  if (/sold out/i.test(text)) return 'Sorry, this just sold out.';
  return text || 'Could not add this item. Please try again.';
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function showMessage(button, text) {
  const message = document.createElement('p');
  message.className = 'cv-cart-picks__message';
  message.textContent = text;
  button.after(message);
  message.scrollIntoView({ block: 'nearest' });
  setTimeout(() => message.remove(), MESSAGE_DURATION);
}

/** @param {HTMLButtonElement} button */
function clearMessage(button) {
  const next = button.nextElementSibling;
  if (next?.classList.contains('cv-cart-picks__message')) next.remove();
}
